// /admin/promos — manage discount codes for client quotes.
// Access: super_admin / admin_licensing / admin_rm (engagement-tier admins).

import { FONTS, RADII, SPACING, STROKES } from "../../../lib/brand";
import { toIsoOrThrow } from "../../../lib/datetime";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { logAdminAction } from "../../../lib/auditLog";
import {
  createPromoCode,
  deletePromoCode,
  generateRandomPromoCode,
  getPromoAnalytics,
  listPromoCodes,
  listRedemptionsForCode,
  updatePromoCode,
  type PromoAppliesTo,
  type PromoCode,
  type PromoCodeInput,
  type PromoDiscountType,
  type PromoRedemption,
  type PromoStatus,
} from "../../../lib/promos";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminModal,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";
import { StatStrip } from "../../../components/aspects/StatStrip";
import { SectionHeader } from "../../../components/aspects/SectionHeader";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

type StatusFilter = "active" | "paused" | "expired" | "all";

function statusTone(status: PromoStatus): StatusPillTone {
  if (status === "active") return "sage";
  if (status === "paused") return "amber";
  return "grey";
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function dateOrEmpty(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function discountSummary(p: PromoCode): string {
  if (p.discount_type === "percent") return `${p.discount_value}% off ${p.applies_to.replace("_", " ")}`;
  return `${formatCents(p.discount_value)} off ${p.applies_to.replace("_", " ")}`;
}

export default function AdminPromosScreen() {
  const [rows, setRows] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listPromoCodes());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const visible = useMemo(() => rows.filter((r) => statusFilter === "all" || r.status === statusFilter), [rows, statusFilter]);
  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Promos"
          title="Promo codes"
          subtitle="Manage discount codes for client quotes. Codes apply at approval time and bake into the client's initial payment."
          actions={<AdminButton theme={THEME} label="+ New code" onPress={() => setNewOpen(true)} />}
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {(["active", "paused", "expired", "all"] as StatusFilter[]).map((s) => (
            <FilterChip key={s} theme={THEME} label={s.charAt(0).toUpperCase() + s.slice(1)} active={statusFilter === s} onPress={() => setStatusFilter(s)} />
          ))}
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading promo codes…" />
          ) : visible.length === 0 ? (
            <AdminEmpty theme={THEME} title="No codes match." body={rows.length === 0 ? "Click + New code to create your first promo." : "Try a different filter."} />
          ) : (
            <View style={{ gap: 8 }}>
              {visible.map((row) => (
                <PromoRow key={row.id} row={row} expanded={row.id === selectedId} onPress={() => setSelectedId(row.id === selectedId ? null : row.id)} />
              ))}
            </View>
          )}
        </View>

        {selected ? (
          <PromoDetailPanel promo={selected} onClose={() => setSelectedId(null)} onSaved={() => { setSelectedId(null); void load(); }} />
        ) : null}
      </View>

      {newOpen ? (
        <NewPromoModal onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); void load(); }} />
      ) : null}
    </ScrollView>
  );
}

function PromoRow({ row, expanded, onPress }: { row: PromoCode; expanded: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        { padding: 14, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: expanded ? THEME.accentBorderHover : THEME.border } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <Text style={{ fontFamily: "SpaceMono", fontSize: 14, color: THEME.accent, letterSpacing: 1.2 }}>{row.code}</Text>
        <StatusPill theme={THEME} label={row.status} tone={statusTone(row.status)} />
        <View style={{ flex: 1 }} />
        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>{discountSummary(row)}</Text>
        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceFaint }}>
          {row.current_use_count}{row.max_total_uses != null ? `/${row.max_total_uses}` : ""} uses
        </Text>
      </View>
      <Text style={{ marginTop: 6, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>{row.description ?? "—"}</Text>
      <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
        Valid {dateOrEmpty(row.valid_from)} → {dateOrEmpty(row.valid_until)}
        {row.required_engagement_model ? ` · ${row.required_engagement_model}` : ""}
        {row.required_tier ? ` · tier ${row.required_tier}` : ""}
      </Text>
    </Pressable>
  );
}

function PromoDetailPanel({ promo, onClose, onSaved }: { promo: PromoCode; onClose: () => void; onSaved: () => void }) {
  const readOnly = promo.status === "expired";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState(promo.code);
  const [description, setDescription] = useState(promo.description ?? "");
  const [internalNotes, setInternalNotes] = useState(promo.internal_notes ?? "");
  const [discountType, setDiscountType] = useState<PromoDiscountType>(promo.discount_type);
  const [discountValue, setDiscountValue] = useState(String(promo.discount_type === "fixed_amount" ? promo.discount_value / 100 : promo.discount_value));
  const [appliesTo, setAppliesTo] = useState<PromoAppliesTo>(promo.applies_to);
  const [maxTotalUses, setMaxTotalUses] = useState(promo.max_total_uses == null ? "" : String(promo.max_total_uses));
  const [maxUsesPerClient, setMaxUsesPerClient] = useState(String(promo.max_uses_per_client));
  const [validFrom, setValidFrom] = useState(promo.valid_from ? promo.valid_from.slice(0, 10) : "");
  const [validUntil, setValidUntil] = useState(promo.valid_until ? promo.valid_until.slice(0, 10) : "");
  const [minOrder, setMinOrder] = useState(promo.min_order_cents == null ? "" : String(promo.min_order_cents / 100));
  const [requiredEngagement, setRequiredEngagement] = useState<"" | "licensing" | "rm">(promo.required_engagement_model ?? "");
  const [requiredTier, setRequiredTier] = useState<"" | "100" | "250" | "500" | "unlimited">(promo.required_tier ?? "");
  const [redemptions, setRedemptions] = useState<PromoRedemption[]>([]);
  const [analytics, setAnalytics] = useState<{ redemption_count: number; total_discount_cents: number; total_revenue_cents: number } | null>(null);

  useEffect(() => {
    void (async () => {
      const [r, a] = await Promise.all([listRedemptionsForCode(promo.id), getPromoAnalytics(promo.id)]);
      setRedemptions(r);
      setAnalytics(a);
    })();
  }, [promo.id]);

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const valueAsCentsOrPercent = discountType === "fixed_amount" ? Math.round(parseFloat(discountValue) * 100) : parseInt(discountValue, 10);
      if (!Number.isFinite(valueAsCentsOrPercent) || valueAsCentsOrPercent < 0) throw new Error("Discount value must be a positive number");
      await updatePromoCode(promo.id, {
        code: code.trim().toUpperCase(),
        description: description.trim() || null,
        internal_notes: internalNotes.trim() || null,
        discount_type: discountType,
        discount_value: valueAsCentsOrPercent,
        applies_to: appliesTo,
        max_total_uses: maxTotalUses.trim() === "" ? null : parseInt(maxTotalUses, 10),
        max_uses_per_client: Math.max(1, parseInt(maxUsesPerClient, 10) || 1),
        valid_from: toIsoOrThrow(validFrom, "Valid from"),
        valid_until: toIsoOrThrow(validUntil, "Valid until"),
        min_order_cents: minOrder.trim() === "" ? null : Math.round(parseFloat(minOrder) * 100),
        required_engagement_model: requiredEngagement === "" ? null : requiredEngagement,
        required_tier: requiredTier === "" ? null : requiredTier,
        required_university_id: promo.required_university_id,
        status: promo.status,
      });
      void logAdminAction({ action: "promo.updated", entityType: "promo_codes", entityId: promo.id, summary: `Updated promo code ${code.trim().toUpperCase()}` });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onTogglePause() {
    setBusy(true);
    setErr(null);
    try {
      const next: PromoStatus = promo.status === "paused" ? "active" : "paused";
      await updatePromoCode(promo.id, { status: next });
      void logAdminAction({ action: "promo.status_changed", entityType: "promo_codes", entityId: promo.id, summary: `Set ${promo.code} status to ${next}` });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    setBusy(true);
    setErr(null);
    try {
      await deletePromoCode(promo.id);
      void logAdminAction({ action: "promo.deleted", entityType: "promo_codes", entityId: promo.id, summary: `Deleted promo code ${promo.code}` });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <View style={{ marginTop: 18, padding: 18, borderRadius: RADII.md, backgroundColor: THEME.surfaceBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 22, color: THEME.textOnSurface }}>Edit promo</Text>
        <StatusPill theme={THEME} label={promo.status} tone={statusTone(promo.status)} />
        {readOnly ? <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>(read-only — expired)</Text> : null}
        <View style={{ flex: 1 }} />
        <AdminButton theme={THEME} label="Close" variant="ghost" size="sm" onPress={onClose} />
      </View>

      {err ? <View style={{ marginTop: 12 }}><AdminError theme={THEME} message={err} /></View> : null}

      {analytics ? (
        <View style={{ marginTop: 14 }}>
          <StatStrip
            theme={THEME}
            eyebrow="Performance"
            cells={[
              { label: "Redemptions", value: analytics.redemption_count },
              { label: "Total discount", value: formatCents(analytics.total_discount_cents) },
              { label: "Revenue (redeemed)", value: formatCents(analytics.total_revenue_cents) },
              { label: "Code uses", value: `${promo.current_use_count}${promo.max_total_uses != null ? `/${promo.max_total_uses}` : ""}` },
            ]}
          />
        </View>
      ) : null}

      <Section label="Code + description">
        <View style={{ flexDirection: "row", gap: 8, alignItems: "flex-end" }}>
          <View style={{ flex: 1 }}>
            <AdminInput theme={THEME} label="Code" value={code} onChangeText={(v) => setCode(v.toUpperCase())} editable={!readOnly} autoCapitalize="characters" />
          </View>
        </View>
        <View style={{ marginTop: 10 }}>
          <AdminInput theme={THEME} label="Description (shown to client at apply time)" value={description} onChangeText={setDescription} multiline editable={!readOnly} />
        </View>
      </Section>

      <Section label="Discount">
        <ChipRow label="Type" value={discountType} options={[{ k: "percent", l: "% off" }, { k: "fixed_amount", l: "$ off" }]} onChange={(v) => setDiscountType(v as PromoDiscountType)} disabled={readOnly} />
        <View style={{ marginTop: 10 }}>
          <AdminInput theme={THEME} label={discountType === "percent" ? "Value (0–100)" : "Value (dollars)"} value={discountValue} onChangeText={setDiscountValue} keyboardType="numeric" editable={!readOnly} />
        </View>
        <ChipRow label="Applies to" value={appliesTo} options={[{ k: "total", l: "Total" }, { k: "fees_only", l: "Fees only" }, { k: "scholarship_only", l: "Scholarship only" }]} onChange={(v) => setAppliesTo(v as PromoAppliesTo)} disabled={readOnly} />
      </Section>

      <Section label="Limits">
        <AdminInput theme={THEME} label="Max total uses (blank = unlimited)" value={maxTotalUses} onChangeText={setMaxTotalUses} keyboardType="numeric" editable={!readOnly} />
        <View style={{ marginTop: 10 }}>
          <AdminInput theme={THEME} label="Max uses per client" value={maxUsesPerClient} onChangeText={setMaxUsesPerClient} keyboardType="numeric" editable={!readOnly} />
        </View>
      </Section>

      <Section label="Validity window">
        <AdminInput theme={THEME} label="Valid from (YYYY-MM-DD, blank = now)" value={validFrom} onChangeText={setValidFrom} editable={!readOnly} autoCapitalize="none" />
        <View style={{ marginTop: 10 }}>
          <AdminInput theme={THEME} label="Valid until (blank = never)" value={validUntil} onChangeText={setValidUntil} editable={!readOnly} autoCapitalize="none" />
        </View>
      </Section>

      <Section label="Eligibility">
        <ChipRow label="Engagement model" value={requiredEngagement} options={[{ k: "", l: "Any" }, { k: "licensing", l: "Licensing" }, { k: "rm", l: "RM" }]} onChange={(v) => setRequiredEngagement(v as "" | "licensing" | "rm")} disabled={readOnly} />
        <ChipRow label="Tier" value={requiredTier} options={[{ k: "", l: "Any" }, { k: "100", l: "100" }, { k: "250", l: "250" }, { k: "500", l: "500" }, { k: "unlimited", l: "Unlimited" }]} onChange={(v) => setRequiredTier(v as "" | "100" | "250" | "500" | "unlimited")} disabled={readOnly} />
        <View style={{ marginTop: 10 }}>
          <AdminInput theme={THEME} label="Min order ($, blank = no minimum)" value={minOrder} onChangeText={setMinOrder} keyboardType="numeric" editable={!readOnly} />
        </View>
      </Section>

      <Section label="Internal notes (admin-only)">
        <AdminInput theme={THEME} label="Internal notes" value={internalNotes} onChangeText={setInternalNotes} multiline editable={!readOnly} />
      </Section>

      <Section label={`Redemption history (last ${redemptions.length})`}>
        {redemptions.length === 0 ? (
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>No redemptions yet.</Text>
        ) : (
          <ListRowGroup theme={THEME}>
            {redemptions.map((r, i) => (
              <ListRow
                key={r.id}
                theme={THEME}
                first={i === 0}
                title={`${r.client_name ?? r.client_email ?? "—"} · ${r.competition_name ?? "—"}`}
                subtitle={`${formatCents(r.amount_discounted_cents)} discount · ${new Date(r.applied_at).toLocaleDateString()}`}
              />
            ))}
          </ListRowGroup>
        )}
      </Section>

      <View style={{ marginTop: 18, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {!readOnly ? <AdminButton theme={THEME} label="Save changes" loading={busy} onPress={() => void onSave()} /> : null}
        {!readOnly ? <AdminButton theme={THEME} label={promo.status === "paused" ? "Resume" : "Pause"} variant="outline" disabled={busy} onPress={() => void onTogglePause()} /> : null}
        {promo.current_use_count === 0 ? <AdminButton theme={THEME} label="Delete" variant="destructive" disabled={busy} onPress={() => void onDelete()} /> : null}
      </View>
    </View>
  );
}

function NewPromoModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState<PromoDiscountType>("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [appliesTo, setAppliesTo] = useState<PromoAppliesTo>("total");
  const [maxTotalUses, setMaxTotalUses] = useState("");
  const [maxUsesPerClient, setMaxUsesPerClient] = useState("1");
  const [validUntil, setValidUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      if (!code.trim()) throw new Error("Code is required");
      const value = discountType === "fixed_amount" ? Math.round(parseFloat(discountValue) * 100) : parseInt(discountValue, 10);
      if (!Number.isFinite(value) || value <= 0) throw new Error("Discount value must be > 0");
      if (discountType === "percent" && value > 100) throw new Error("Percent must be ≤ 100");
      const input: PromoCodeInput = {
        code: code.trim().toUpperCase(),
        description: description.trim() || null,
        internal_notes: null,
        discount_type: discountType,
        discount_value: value,
        applies_to: appliesTo,
        max_total_uses: maxTotalUses.trim() === "" ? null : parseInt(maxTotalUses, 10),
        max_uses_per_client: Math.max(1, parseInt(maxUsesPerClient, 10) || 1),
        valid_from: null,
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
        min_order_cents: null,
        required_engagement_model: null,
        required_tier: null,
        required_university_id: null,
        status: "active",
      };
      const id = await createPromoCode(input);
      void logAdminAction({ action: "promo.created", entityType: "promo_codes", entityId: id, summary: `Created promo code ${input.code}` });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminModal
      theme={THEME}
      visible
      onClose={onClose}
      maxWidth={560}
      title="New promo code"
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Create code" loading={busy} onPress={() => void onSave()} />
        </>
      }
    >
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: "row", gap: 8, alignItems: "flex-end" }}>
          <View style={{ flex: 1 }}>
            <AdminInput theme={THEME} label="Code" value={code} onChangeText={(v) => setCode(v.toUpperCase())} autoCapitalize="characters" />
          </View>
          <AdminButton theme={THEME} label="Generate" variant="outline" size="sm" onPress={() => setCode(generateRandomPromoCode(8))} />
        </View>
        <AdminInput theme={THEME} label="Description (shown to client)" value={description} onChangeText={setDescription} multiline />
        <ChipRow label="Type" value={discountType} options={[{ k: "percent", l: "% off" }, { k: "fixed_amount", l: "$ off" }]} onChange={(v) => setDiscountType(v as PromoDiscountType)} />
        <AdminInput theme={THEME} label={discountType === "percent" ? "Value (0–100)" : "Value (dollars)"} value={discountValue} onChangeText={setDiscountValue} keyboardType="numeric" />
        <ChipRow label="Applies to" value={appliesTo} options={[{ k: "total", l: "Total" }, { k: "fees_only", l: "Fees only" }, { k: "scholarship_only", l: "Scholarship only" }]} onChange={(v) => setAppliesTo(v as PromoAppliesTo)} />
        <AdminInput theme={THEME} label="Max total uses (blank = unlimited)" value={maxTotalUses} onChangeText={setMaxTotalUses} keyboardType="numeric" />
        <AdminInput theme={THEME} label="Max uses per client" value={maxUsesPerClient} onChangeText={setMaxUsesPerClient} keyboardType="numeric" />
        <AdminInput theme={THEME} label="Valid until (YYYY-MM-DD, blank = never)" value={validUntil} onChangeText={setValidUntil} autoCapitalize="none" />
        {err ? <AdminError theme={THEME} message={err} /> : null}
      </View>
    </AdminModal>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 18 }}>
      <SectionHeader theme={THEME} title={label} tone="dark" />
      {children}
    </View>
  );
}

function ChipRow({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: { k: string; l: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <View style={{ marginTop: 10 }}>
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: THEME.textOnSurfaceDim, marginBottom: 6 }}>{label}</Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, opacity: disabled ? 0.5 : 1 }}>
        {options.map((o) => (
          <FilterChip key={o.k || "any"} theme={THEME} label={o.l} active={o.k === value} onPress={() => { if (!disabled) onChange(o.k); }} />
        ))}
      </View>
    </View>
  );
}
